/**
 * Marketplace API Client
 */

import { api } from '@/lib/api-client';
import type {
  MarketplaceListing,
  CreateListingInput,
  UpdateListingInput,
  ListingResponse,
  ListingListResponse,
  MarketplaceInstall,
  InstallOptions,
  InstallResponse,
  InstallListResponse,
  MarketplaceReview,
  CreateReviewInput,
  ReviewResponse,
  ReviewListResponse,
  CategoryListResponse,
  VersionListResponse,
  FeaturedResponse,
  SearchParams,
} from './types';

const MARKETPLACE_BASE = '/api/v1/marketplace';

// ============================================
// Browse & Search
// ============================================

/**
 * Search marketplace listings
 */
export async function searchListings(params: SearchParams = {}): Promise<ListingListResponse> {
  const queryParams: Record<string, string> = {};

  if (params.query) queryParams.query = params.query;
  if (params.listing_type) queryParams.listing_type = params.listing_type;
  if (params.category) queryParams.category = params.category;
  if (params.subcategory) queryParams.subcategory = params.subcategory;
  if (params.tags?.length) queryParams.tags = params.tags.join(',');
  if (params.price_type) queryParams.price_type = params.price_type;
  if (params.min_rating !== undefined) queryParams.min_rating = String(params.min_rating);
  if (params.sort_by) queryParams.sort_by = params.sort_by;
  if (params.sort_order) queryParams.sort_order = params.sort_order;
  if (params.limit) queryParams.limit = String(params.limit);
  if (params.offset) queryParams.offset = String(params.offset);

  return api.get<ListingListResponse>(MARKETPLACE_BASE, { params: queryParams });
}

/**
 * Get featured listings
 */
export async function getFeatured(): Promise<FeaturedResponse> {
  return api.get<FeaturedResponse>(`${MARKETPLACE_BASE}/featured`);
}

/**
 * Get categories
 */
export async function getCategories(): Promise<CategoryListResponse> {
  return api.get<CategoryListResponse>(`${MARKETPLACE_BASE}/categories`);
}

/**
 * Get listing by slug
 */
export async function getListing(slug: string): Promise<ListingResponse> {
  return api.get<ListingResponse>(`${MARKETPLACE_BASE}/${slug}`);
}

/**
 * Get listing versions
 */
export async function getListingVersions(listingId: string): Promise<VersionListResponse> {
  return api.get<VersionListResponse>(`${MARKETPLACE_BASE}/listings/${listingId}/versions`);
}

// ============================================
// Install Management
// ============================================

/**
 * Install a listing
 */
export async function installListing(
  listingId: string,
  options: InstallOptions = {}
): Promise<InstallResponse> {
  return api.post<InstallResponse>(`${MARKETPLACE_BASE}/${listingId}/install`, options);
}

/**
 * Uninstall a listing
 */
export async function uninstallListing(listingId: string): Promise<{ success: boolean }> {
  return api.delete(`${MARKETPLACE_BASE}/${listingId}/install`);
}

/**
 * Get my installations
 */
export async function getMyInstalls(): Promise<InstallListResponse> {
  return api.get<InstallListResponse>(`${MARKETPLACE_BASE}/installs`);
}

/**
 * Check for updates
 */
export async function checkUpdates(): Promise<{
  success: boolean;
  data: Array<{
    install_id: string;
    listing_id: string;
    current_version: string;
    latest_version: string;
  }>;
}> {
  return api.get(`${MARKETPLACE_BASE}/installs/check-updates`);
}

/**
 * Update an installation
 */
export async function updateInstall(installId: string): Promise<InstallResponse> {
  return api.post<InstallResponse>(`${MARKETPLACE_BASE}/installs/${installId}/update`);
}

// ============================================
// Reviews
// ============================================

/**
 * Get reviews for a listing
 */
export async function getReviews(
  listingId: string,
  params?: { sort_by?: 'helpful' | 'newest' | 'rating'; limit?: number; offset?: number }
): Promise<ReviewListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.sort_by) queryParams.sort_by = params.sort_by;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.offset) queryParams.offset = String(params.offset);

  return api.get<ReviewListResponse>(`${MARKETPLACE_BASE}/${listingId}/reviews`, {
    params: queryParams,
  });
}

/**
 * Create a review
 */
export async function createReview(
  listingId: string,
  review: CreateReviewInput
): Promise<ReviewResponse> {
  return api.post<ReviewResponse>(`${MARKETPLACE_BASE}/${listingId}/reviews`, review);
}

/**
 * Update a review
 */
export async function updateReview(
  listingId: string,
  review: CreateReviewInput
): Promise<ReviewResponse> {
  return api.put<ReviewResponse>(`${MARKETPLACE_BASE}/${listingId}/reviews`, review);
}

/**
 * Delete a review
 */
export async function deleteReview(listingId: string): Promise<{ success: boolean }> {
  return api.delete(`${MARKETPLACE_BASE}/${listingId}/reviews`);
}

/**
 * Vote on review helpfulness
 */
export async function voteReview(
  reviewId: string,
  isHelpful: boolean
): Promise<{ success: boolean }> {
  return api.post(`${MARKETPLACE_BASE}/reviews/${reviewId}/vote`, {
    is_helpful: isHelpful,
  });
}

// ============================================
// Publisher (My Listings)
// ============================================

/**
 * Get my listings
 */
export async function getMyListings(): Promise<ListingListResponse> {
  return api.get<ListingListResponse>(`${MARKETPLACE_BASE}/listings`);
}

/**
 * Create a new listing
 */
export async function createListing(input: CreateListingInput): Promise<ListingResponse> {
  return api.post<ListingResponse>(`${MARKETPLACE_BASE}/listings`, input);
}

/**
 * Update a listing
 */
export async function updateListing(
  listingId: string,
  input: UpdateListingInput
): Promise<ListingResponse> {
  return api.put<ListingResponse>(`${MARKETPLACE_BASE}/listings/${listingId}`, input);
}

/**
 * Delete a listing
 */
export async function deleteListing(listingId: string): Promise<{ success: boolean }> {
  return api.delete(`${MARKETPLACE_BASE}/listings/${listingId}`);
}

/**
 * Submit listing for review
 */
export async function submitForReview(listingId: string): Promise<ListingResponse> {
  return api.post<ListingResponse>(`${MARKETPLACE_BASE}/listings/${listingId}/submit`);
}

/**
 * Publish a new version
 */
export async function publishVersion(
  listingId: string,
  version: string,
  changelog?: string
): Promise<ListingResponse> {
  return api.post<ListingResponse>(`${MARKETPLACE_BASE}/listings/${listingId}/publish`, {
    version,
    changelog,
  });
}

/**
 * Archive a listing
 */
export async function archiveListing(listingId: string): Promise<ListingResponse> {
  return api.post<ListingResponse>(`${MARKETPLACE_BASE}/listings/${listingId}/archive`);
}

// ============================================
// Export all functions
// ============================================

export default {
  // Browse
  searchListings,
  getFeatured,
  getCategories,
  getListing,
  getListingVersions,
  // Install
  installListing,
  uninstallListing,
  getMyInstalls,
  checkUpdates,
  updateInstall,
  // Reviews
  getReviews,
  createReview,
  updateReview,
  deleteReview,
  voteReview,
  // Publisher
  getMyListings,
  createListing,
  updateListing,
  deleteListing,
  submitForReview,
  publishVersion,
  archiveListing,
};
